import { isEmpty, isEqual, reject } from "lodash/fp";

import Table, { TableBody, TableHead } from "@app/analytics/components/Table";
import useRollupContext from "@app/analytics/hooks/meetops/useRollupContext";
import useReportsTable from "@app/analytics/MeetingReports/stores/reportsTable";
import type {
  OnOpenReportHandler,
  Report,
  ReportView,
} from "@app/analytics/MeetingReports/types";
import { isTopicReport, isUnseen } from "@app/analytics/MeetingReports/utils";
import useDataTable, { HeadCell } from "@app/common/hooks/useDataTable";

import MeetingReportTableRow from "./MeetingReportTableRow";
import ReportsTableEmptyState from "./ReportsTableEmptyState";
import TopicReportTableRow from "./TopicReportTableRow";
import { ReportSortUtils } from "./utils";

const HEAD_CELLS: HeadCell<Report>[] = [
  {
    id: "source",
    label: "Source",
    align: "center",
    sortBy: ReportSortUtils.source,
  },
  {
    id: "title",
    label: "Report",
    sortBy: ReportSortUtils.title,
  },
  {
    id: "date",
    label: "Date & Time",
    sortBy: ReportSortUtils.date,
  },
  {
    id: "score",
    label: "Read Score",
    sortBy: ReportSortUtils.readScore,
  },
  {
    id: "tags",
    label: "Tags",
    sortable: false,
  },
  {
    id: "ownerName",
    label: "Owner",
  },
  {
    id: "actions",
    label: "",
    sortable: false,
  },
];

const TOPIC_REPORTS_ONLY_HEAD_CELLS = reject({ id: "score" }, HEAD_CELLS);

interface ReportsTableProps {
  isLoading: boolean;
  lastSeenDate?: string | null;
  onOpenReport: OnOpenReportHandler;
  pagination: {
    currentPage: number;
    pageSize: number;
  };
  reports: Report[];
  /* Reports that are rendered at the top of the reports table (e.g. processing topic report) */
  stickyReports?: Report[];
}

const isViewingOnlyTopicReports = (reportViews: ReportView[]) =>
  isEqual(reportViews, ["topic"]);

const ReportsTable = ({
  isLoading,
  lastSeenDate,
  onOpenReport,
  pagination,
  reports,
  stickyReports,
}: ReportsTableProps) => {
  const { filterBy } = useRollupContext();
  const topicReportsOnly = useReportsTable((state) =>
    isViewingOnlyTopicReports(state.reportViews)
  );

  const headCells = topicReportsOnly
    ? TOPIC_REPORTS_ONLY_HEAD_CELLS
    : HEAD_CELLS;

  const { handleSort, order, orderBy, sortedData } = useDataTable(
    reports,
    headCells,
    {
      defaultOrderBy: "date",
      defaultOrder: "desc",
      stickyData: stickyReports,
    }
  );

  const reportsForPage = sortedData.slice(
    pagination.pageSize * pagination.currentPage,
    pagination.pageSize * (pagination.currentPage + 1)
  );

  const showAttendance = filterBy === "individual" || filterBy === "me";

  return (
    <Table>
      <TableHead
        headCells={headCells}
        onSort={handleSort}
        order={order}
        orderBy={orderBy}
      />
      <TableBody loading={isLoading}>
        {isEmpty(reportsForPage) && <ReportsTableEmptyState />}
        {reportsForPage.map((report) => {
          const reportIsUnseen = isUnseen(report, lastSeenDate);
          if (isTopicReport(report)) {
            return (
              <TopicReportTableRow
                key={`topic-${report.id}`}
                report={report}
                isUnseen={reportIsUnseen}
                onOpenReport={onOpenReport}
                withScore={!topicReportsOnly}
              />
            );
          }

          return (
            <MeetingReportTableRow
              key={`session-${report.id}`}
              report={report}
              isUnseen={reportIsUnseen}
              onOpenReport={onOpenReport}
              showAttendance={showAttendance}
            />
          );
        })}
      </TableBody>
    </Table>
  );
};

export default ReportsTable;
